import { useEffect, useRef } from 'react';
import { metrics } from '../metrics/client';

/**
 * Registers a DOM element for IntersectionObserver-based visibility tracking.
 *
 * Returns a ref to attach to the target element. When the element enters or
 * leaves the viewport (at 10% threshold), a clickstream `visibility` event
 * is emitted with the given `name` and `visible: true/false`.
 *
 * The element is automatically observed on mount and unobserved on unmount
 * or when `name` changes. The `data-metrics-name` attribute is set on the
 * element so the IntersectionObserver callback in MetricsProvider can
 * identify it.
 *
 * @param {string} name — Identifier for the tracked section (e.g., 'ai-notes-panel').
 *   Used as the `name` field in the visibility event metadata.
 * @returns {React.RefObject} — Attach this ref to the container element to track.
 *
 * @example
 * function AINotesPanel() {
 *   const ref = useVisibilityTracking('ai-notes-panel');
 *   return <div ref={ref}>...</div>;
 * }
 */
export const useVisibilityTracking = (name) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !name) return;

    el.setAttribute('data-metrics-name', name);
    metrics.observe(el);

    return () => {
      metrics.unobserve(el);
    };
  }, [name]);

  return ref;
};
